/**
 * StudySync Participant Flow Component
 * Main enrollment flow for study participants
 */

import React, { useState } from 'react';
import { API_CONFIG } from '../../config/api';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Science,
  CheckCircle,
} from '@mui/icons-material';
import { AuthService } from '../../services/authService';
import LoadingSpinner from '../common/LoadingSpinner';
import type { StudyVerificationResult } from '../../types';

const steps = ['Enter Study Code', 'Study Information', 'Personal Information', 'Fitbit Authorization'];

const ParticipantFlow: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [studyCode, setStudyCode] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [studyInfo, setStudyInfo] = useState<StudyVerificationResult | null>(null);

  const handleStudyCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!studyCode.trim()) {
      setError('Please enter a study code');
      return;
    }

    const formattedCode = AuthService.formatStudyCode(studyCode);
    if (!AuthService.validateStudyCode(formattedCode)) {
      setError('Study code must be 6-8 characters (letters and numbers only)');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await AuthService.verifyStudyCode(formattedCode);
      if (response.success && response.study) {
        setStudyInfo(response.study);
        setStudyCode(formattedCode);
        setActiveStep(1);
      } else {
        setError(response.error || 'Invalid study code. Please check and try again.');
      }
    } catch (err: any) {
      setError('Failed to verify study code: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleStudyInfoNext = () => {
    setActiveStep(2);
  };

  const handlePersonalInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !firstName.trim() || !lastName.trim() || !dateOfBirth) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate date of birth (must be at least 18 years old)
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      setError('You must be at least 18 years old to participate');
      return;
    }

    // Skip email verification and go directly to Fitbit authorization
    await handleDirectFitbitAuth();
  };

  const handleDirectFitbitAuth = async () => {
    if (!studyCode || !email || !firstName || !lastName || !dateOfBirth) {
      setError('Missing required information');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Create participant and get Fitbit auth URL directly
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/participants/enroll-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: dateOfBirth,
          studyCode: studyCode,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to enroll participant');
        return;
      }

      // Redirect directly to Fitbit authorization
      if (data.fitbitAuthUrl) {
        window.location.href = data.fitbitAuthUrl;
      } else {
        setError('Failed to get Fitbit authorization URL');
      }
    } catch (err: any) {
      setError('Failed to enroll participant: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };


  if (loading && activeStep === 0) {
    return <LoadingSpinner message="Verifying study code..." />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Science sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Join Research Study
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Connect your Fitbit to contribute to health research
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Step 1: Enter Study Code */}
          {activeStep === 0 && (
            <Box component="form" onSubmit={handleStudyCodeSubmit}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Enter Your Study Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please enter the study code provided by your researcher (6-8
                characters). This web flow is for Fitbit enrollment.
              </Typography>
              <TextField
                fullWidth
                label="Study Code"
                value={studyCode}
                onChange={(e) => setStudyCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 8 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify Study Code'}
              </Button>
            </Box>
          )}

          {/* Step 2: Study Information */}
          {activeStep === 1 && studyInfo && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Study Information
              </Typography>
              
              <Card sx={{ mb: 3, backgroundColor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {studyInfo.studyName}
                  </Typography>
                  {studyInfo.studyDescription && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {studyInfo.studyDescription}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Study Code:</strong> {studyCode}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Researcher:</strong> {studyInfo.researcherName || 'Research Team'}
                  </Typography>
                </CardContent>
              </Card>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please review the study information above before proceeding.
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleStudyInfoNext}
              >
                Continue
              </Button>
            </Box>
          )}

          {/* Step 3: Personal Information */}
          {activeStep === 2 && (
            <Box component="form" onSubmit={handlePersonalInfoSubmit}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Personal Information
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please provide your personal information to participate in this study.
              </Typography>
              
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                sx={{ mb: 3 }}
                InputLabelProps={{ shrink: true }}
                required
                helperText="You must be at least 18 years old to participate"
              />
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Enrolling...' : 'Continue to Fitbit Authorization'}
              </Button>
            </Box>
          )}

          {/* Step 4: Fitbit Authorization */}
          {activeStep === 3 && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Redirecting to Fitbit Authorization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                You will be redirected to Fitbit to authorize data sharing for this study.
                After authorization, your enrollment will be complete!
              </Typography>
              
              <Card sx={{ mb: 3, backgroundColor: 'info.light', color: 'info.contrastText' }}>
                <CardContent>
                  <Typography variant="body2">
                    <strong>Next Steps:</strong><br />
                    1. You'll be redirected to Fitbit<br />
                    2. Log in to your Fitbit account<br />
                    3. Authorize data sharing<br />
                    4. Return to complete enrollment
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ParticipantFlow;
